import { StyleSheet, css } from 'aphrodite';
import { type FC, type JSX, useState } from 'react';

import { HexButton } from '../../components/HexButton';
import { BookedGraphic } from '../../components/svgs/BookedGraphic';
import { HexGraphic } from '../../components/svgs/HexGraphic';
import { HookRejectedGraphic } from '../../components/svgs/HookRejectedGraphic';
import { QualifiedPitchGraphic } from '../../components/svgs/QualifiedPitchGraphic';
import { QuickPitchGraphic } from '../../components/svgs/QuickPitchGraphic';
import { StumpedGraphic } from '../../components/svgs/StumpedGraphic';
import { FieldKey } from '../../viewModels/form';
import { IconChevronRight } from '../ui/icons';
import type { PhoneCallOutcome, PhoneCallOutcomeGroup } from './types';

export type OutcomePickerProps = {
	/** Step 1 mode: top-level list. Step 2 mode: drill-down with selectedGroup set. */
	mode: 'top-level' | 'drill-down';
	/**
	 * Top-level shortcuts (for `mode='top-level'`). The flat curated list from
	 * `useOutcomes.outcomeShortcuts` — NOT raw outcome groups.
	 */
	outcomes: PhoneCallOutcome[];
	/** Selected group whose children are rendered (for `mode='drill-down'`). */
	selectedGroup?: PhoneCallOutcomeGroup;
	/** Click handler when the user picks a top-level outcome (or the Connected entry). */
	onPickOutcome: (outcome: PhoneCallOutcome) => void;
	/** Click handler when the user picks a child outcome from the drill-down. */
	onPickChild: (child: PhoneCallOutcome) => void;
	/** Back link in drill-down mode. */
	onBack?: () => void;
	/** Whether outcomes are still loading. */
	isLoading?: boolean;
	/**
	 * Eyebrow text rendered above the top-level list. Defaults to
	 * "Last Call Outcome?" for parity with the legacy queue's pending-outcome
	 * eyebrow. The CSS already uppercases via `textTransform`, so callers can
	 * pass mixed-case strings.
	 */
	eyebrow?: string;
};

/**
 * Step 1 + Step 2 of the wrap-up workflow.
 *
 * - `mode='top-level'`: vertical list of curated outcome shortcuts, each with
 *   a colored dot (from `hexColor`); the `Connected` entry shows a chevron.
 * - `mode='drill-down'`: a 2-column grid of `<HexButton>` tiles with the
 *   per-outcome SVG graphics (Booked / HookRejected / QualifiedPitch /
 *   QuickPitch / Stumped) and a `<PointsBubble>`. Mirrors
 *   `aida/components/QueueNoteEditor/ConnectedOptions/index.tsx:96-132`.
 */
export const OutcomePicker: FC<OutcomePickerProps> = ({
	mode,
	outcomes,
	selectedGroup,
	onPickOutcome,
	onPickChild,
	onBack,
	isLoading,
	eyebrow = 'Last Call Outcome?',
}) => {
	if (isLoading) {
		return (
			<div data-testid='outcome-picker-loading' className={css(styles.loading)} aria-live='polite'>
				Loading outcomes…
			</div>
		);
	}

	if (mode === 'drill-down' && selectedGroup) {
		return (
			<div className={css(styles.root)}>
				<button type='button' onClick={onBack} className={css(styles.backLink)}>
					← Back
				</button>
				<div className={css(styles.eyebrow)}>SELECT A STATUS:</div>
				<div className={css(styles.grid)}>
					{selectedGroup.children.map(child => (
						<HexTile key={child.id ?? child.name} outcome={child} onClick={() => onPickChild(child)} />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className={css(styles.root)}>
			<div className={css(styles.eyebrow)}>{eyebrow}</div>
			<div className={css(styles.list)}>
				{outcomes.map(outcome => {
					const isConnected = outcome.name === FieldKey.Connected;
					return (
						<button
							key={outcome.id ?? outcome.name}
							type='button'
							onClick={() => onPickOutcome(outcome)}
							className={css(styles.row)}
						>
							<span className={css(styles.dot)} style={{ background: outcome.hexColor ?? 'var(--ink-muted)' }} />
							<span className={css(styles.rowLabel)}>{outcome.label}</span>
							{isConnected && (
								<span className={css(styles.chev)} aria-hidden>
									<IconChevronRight size={12} />
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

/**
 * Returns the per-outcome illustrated graphic used in the drill-down hex tile.
 * Mirrors `ConnectedOptions/index.tsx:50-67`. Outcomes without a dedicated
 * graphic (e.g. tenant-custom names) fall through to `null` — `HexButton`
 * still renders the hex shape and label.
 */
function getOutcomeGraphic(outcome: PhoneCallOutcome, animate: boolean): JSX.Element | null {
	switch (outcome.name) {
		case FieldKey.Booked:
		case FieldKey.NewDemo:
			return <BookedGraphic animate={animate} />;
		case FieldKey.HookRejected:
			return <HookRejectedGraphic animate={animate} />;
		case FieldKey.QuickPitch:
			return <QuickPitchGraphic animate={animate} />;
		case FieldKey.QualityPitch:
		case FieldKey.QualifiedPitch:
			return <QualifiedPitchGraphic animate={animate} />;
		case FieldKey.Stumped:
			return <StumpedGraphic animate={animate} />;
		default:
			return null;
	}
}

type HexTileProps = {
	outcome: PhoneCallOutcome;
	onClick: () => void;
};

/**
 * Hover-driven wrapper around `<HexButton>` that lights the per-outcome
 * graphic up on mouse-enter (legacy uses `useLambda` + per-outcome state;
 * a single `useState` suffices here for visual parity).
 */
const HexTile: FC<HexTileProps> = ({ outcome, onClick }) => {
	const [hovered, setHovered] = useState(false);
	const graphic = getOutcomeGraphic(outcome, hovered) ?? <HexGraphic animate={false} selected={false} />;
	return (
		<HexButton
			graphic={graphic}
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			points={outcome.points}
			selected={false}
			text={outcome.label}
		/>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		padding: '14px 18px',
		gap: 12,
	},
	eyebrow: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '7px 20px',
		borderRadius: 8,
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		textAlign: 'left',
		color: 'var(--ink)',
		transition: 'background 120ms',
		':hover': {
			background: 'var(--hover)',
		},
	},
	rowLabel: {
		flex: 1,
		fontSize: 13,
	},
	dot: {
		width: 16,
		height: 16,
		borderRadius: 999,
		flexShrink: 0,
		display: 'inline-block',
	},
	chev: {
		color: 'var(--ink-faint)',
		display: 'inline-flex',
	},
	backLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		cursor: 'pointer',
		fontSize: 12,
		fontWeight: 500,
		padding: 0,
	},
	grid: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 12,
		justifyContent: 'center',
	},
	loading: {
		padding: '14px 18px',
		color: 'var(--ink-muted)',
		fontSize: 13,
	},
});
